import { ArrowRight } from 'lucide-react';
import { Button } from '@shadcn/ui/components/ui/button';
import { ContactCard } from './ContactCard';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from '../../../../routes/paths';
import { GET_CONTACTS_RECENTLY_CHANGED_JOBS } from '@zaplify/graphql';
import { useQuery } from '@apollo/client';
import { cn } from '@shadcn/ui/lib/utils/cn.js';
import { Link } from 'react-router-dom';
import { ProspectDataExperienceDto } from '@zaplify/prospects';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useSidebars } from '@shadcn/ui/hooks/use-sidebars';

function buildOccupationStartDatePattern(months: string[], year: string) {
    return `"occupationStartDate"\\s*:\\s*"[^"]*(${months.join('|')})[^"]*${year}[^"]*"`;
}

const determineJobChangeType = (experiences: ProspectDataExperienceDto[]): 'promotion' | 'new-company' => {
    if (!experiences || experiences.length < 2) {
        return 'new-company';
    }

    // Sort experiences by start date (most recent first)
    const sortedExperiences = [...experiences]
        ?.filter((exp) => exp.organizationName)
        .sort((a, b) => {
            const aDate = a.occupationStartDate ? new Date(a.occupationStartDate) : new Date(0);
            const bDate = b.occupationStartDate ? new Date(b.occupationStartDate) : new Date(0);
            return bDate.getTime() - aDate.getTime();
        });

    const currentExperience = sortedExperiences[0];
    const currentCompany = currentExperience.organizationName?.toLowerCase().trim();

    if (!currentCompany || !currentExperience.occupationStartDate) {
        return 'new-company';
    }

    // Find the most recent previous experience with the same company
    const sameCompanyExperiences = sortedExperiences
        .slice(1) // Skip current experience
        .filter((exp) => exp.organizationName?.toLowerCase().trim() === currentCompany)
        .filter((exp) => exp.occupationEndDate); // Only consider experiences with end dates

    if (sameCompanyExperiences.length === 0) {
        return 'new-company';
    }

    // Get the most recent previous experience at the same company
    const lastSameCompanyExperience = sameCompanyExperiences[0];

    // Check if the current experience started within 2 months of the last same-company experience ending
    if (
        dayjs(currentExperience.occupationStartDate).isBefore(
            dayjs(lastSameCompanyExperience.occupationEndDate!).subtract(2, 'month')
        )
    ) {
        return 'promotion';
    }

    return 'new-company';
};

export const NetworkCard = () => {
    const navigate = useNavigate();
    const handleViewActions = () => {
        const contactIds = contacts?.map((contact) => contact.id) || [];
        const searchParams = new URLSearchParams();
        if (contactIds.length > 0) {
            searchParams.set('contactIds', contactIds.join(','));
        }
        navigate(`${paths.NEW.ACTION_FEED_V3}?${searchParams.toString()}`);
    };

    const { data, loading } = useQuery(GET_CONTACTS_RECENTLY_CHANGED_JOBS, {
        variables: {
            pattern: buildOccupationStartDatePattern(
                [
                    dayjs().subtract(2, 'month').format('MMM'),
                    dayjs().subtract(1, 'month').format('MMM'),
                    dayjs().format('MMM'),
                ],
                dayjs().format('YYYY')
            ),
        },
    });

    const contacts = useMemo(() => {
        return data?.Contacts.map((contact) => {
            const jobChangeType = determineJobChangeType(contact.experiences);
            const jobStartDate = contact.experiences[0]?.occupationStartDate;
            return {
                ...contact,
                jobChangeType,
                jobStartDate,
            };
        });
    }, [data]);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="w-full max-w-7xl mx-auto p-6">
            <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl p-2">
                {/* Gradient border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-400 via-blue-500 to-cyan-400 p-[2px]">
                    <div className="h-full w-full rounded-3xl bg-white"></div>
                </div>

                {/* Main content */}
                <div className="relative z-10 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
                    {/* Subtle background elements */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-10 left-10 w-32 h-32 bg-purple-200 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-200 rounded-full blur-3xl"></div>
                        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-cyan-200 rounded-full blur-2xl"></div>
                    </div>

                    {/* Header Section - More compact */}
                    <div className="relative z-10 text-center mb-8">
                        <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
                            Your Network is Moving Up! 🚀
                        </h1>
                        <p className="text-lg text-gray-600 mb-2">{contacts?.length} connections landed new roles</p>
                        <div className="flex justify-center items-center gap-2">
                            <span className="bg-gradient-to-r from-purple-100 to-blue-100 px-4 py-2 rounded-full text-gray-700 border border-gray-200">
                                📈 Career Growth Alert
                            </span>
                        </div>
                    </div>

                    {/* Stats Section - Horizontal layout */}
                    <div className="relative z-10 flex justify-center gap-8 mb-8">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-200 text-center min-w-[120px]">
                            <div className="text-2xl font-bold text-purple-600">{contacts.length}</div>
                            <div className="text-gray-600 text-sm">New Roles</div>
                        </div>
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-200 text-center min-w-[120px]">
                            <div className="text-2xl font-bold text-green-600">
                                {contacts?.filter((contact) => contact.jobChangeType === 'promotion').length}
                            </div>
                            <div className="text-gray-600 text-sm">Promotions</div>
                        </div>
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-200 text-center min-w-[120px]">
                            <div className="text-2xl font-bold text-blue-600">
                                {contacts?.filter((contact) => contact.jobChangeType === 'new-company').length}
                            </div>
                            <div className="text-gray-600 text-sm">New Companies</div>
                        </div>
                    </div>

                    {/* Contacts Grid - Optimized for wider layout */}
                    <div className="relative z-10 mb-8">
                        <h2 className="text-xl font-bold mb-6 text-center text-gray-700">Your Top Movers & Shakers</h2>
                        <div className="grid grid-cols-5 gap-4 max-w-5xl mx-auto">
                            {contacts?.map((contact, index) => (
                                <ContactCard
                                    key={contact.id}
                                    contact={contact}
                                    index={index}
                                    jobChangeType={contact.jobChangeType}
                                    jobStartDate={contact.jobStartDate}
                                />
                            ))}
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="relative z-10 text-center">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto border border-gray-200">
                            <h3 className="text-xl font-bold mb-3 text-gray-700">Perfect Time to Congratulate! 🎉</h3>
                            <p className="text-gray-600 mb-4 text-sm">
                                Career moves are golden opportunities to reconnect and strengthen relationships
                            </p>
                            <Button
                                onClick={handleViewActions}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 font-semibold py-3 px-6 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                            >
                                Send Congratulations
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
